<template>
  <div
    class="floating-view"
    :style="floatingViewStyle"
  >
    <div class="header-view">
      <div class="header-list profile-img-clickable" @click.stop="goToBusinessProfile()">
        <span class="image-view">
          <img :src="profileImage" alt="User profile" @error="useDefaultAvatar"/>
        </span>
        <span class="name-row">
          <span class="name">{{ service.userDetails?.userName || "Unknown User" }}</span>
          <span
            v-if="service.userDetails && (service.userDetails.emailVerified === true || service.userDetails.phoneVerified === true)"
            class="verify-tick"
            title="Verified Account"
            style="margin-left:2px; display:inline-flex; align-items:center; vertical-align:middle;"
          >
            <svg width="26" height="26" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" style="display:block;">
              <rect x="8" y="20" width="48" height="28" rx="4" fill="#E74C3C"/>
              <polygon points="32,38 8,20 56,20" fill="#C0392B"/>
              <polygon points="32,38 8,48 8,20" fill="#000" opacity="0.07"/>
              <polygon points="32,38 56,48 56,20" fill="#000" opacity="0.07"/>
              <circle cx="32" cy="20" r="12" fill="#ECECEC"/>
              <circle cx="32" cy="20" r="8" fill="#26A69A"/>
              <path d="M29 20l3 3 5-5" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </span>
      </div>
      <div class="header-actions">
        <button class="icon-btn" @click="toggleMinimize">
          <img
            v-if="!isMinimized"
            src="../icons/minus-round-line-icon.svg"
            class="icon"
            alt="Minimize"
          />
          <img
            v-else
            src="../icons/maximize-square-3-svgrepo-com.svg"
            class="icon"
            alt="Maximize"
          />
        </button>
        <button class="icon-btn" @click="$emit('close')">
          <img src="../icons/close-svgrepo-com.svg" class="icon" alt="Close" />
        </button>
      </div>
    </div>

    <div v-if="!isMinimized" class="main-view" ref="chatContainer">
      <template v-for="(group, date) in groupedMessages" :key="date">
        <div class="date-separator">{{ date }}</div>
        
        <div
          v-for="message in group"
          :key="message.id || message.messageId"
          :class="['message', message.sender === 'me' ? 'message-outgoing' : 'message-incoming']"
        >
          <!-- Incoming message avatar -->
          <!-- Incoming messages are always from the ad owner, whose picture is
               already resolved into profileImage (falling back to the default
               avatar). @error covers a stored URL that 404s. -->
          <img
            v-if="message.sender !== 'me'"
            class="avatar"
            :src="profileImage"
            alt="Message sender avatar"
            @error="useDefaultAvatar"
          />

          <div class="message-bubble">
            <div v-html="formatMessage(message.message)"></div>

            <!-- Check for image attachments -->
            <div v-if="message.images && message.images.length" class="message-images">
              <img v-for="(img, index) in message.images" :key="index" :src="img" alt="Attached image" />
            </div>

            <span class="message-time">{{ formatDate(message.sendDate) }}</span>
          </div>
        </div>
      </template>
      <div v-if="showServiceBox" class="reply-preview">
        <div class="service-card">
          <div class="service-info">
            <div class="service-title">{{ service.servicesName }}</div>
            <div class="service-location">{{ service.location }}</div>
          </div>


        </div>
      </div>


    </div>

    <!-- Bottom input -->
    <div v-if="!isMinimized" class="bottom-view">
      <div class="bottom-box">
        <input
          class="input-box-text"
          type="text"
          placeholder="Message"
          v-model="chatMessage"
          @keyup.enter="sendMessage"
        />
        <button class="btn-view" @click="sendMessage" title="Send">
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18.333 1.667L9.167 10.833M18.333 1.667L12.5 18.333L9.167 10.833M18.333 1.667L1.667 7.5L9.167 10.833"
              stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    </div>

  </div>
</template>

<script>
import axios from "@/utils/axiosinstance";
import { WEB_ORIGIN } from "@/config/apiBase";
import { getRecaptchaToken } from "@/utils/recaptcha";
import { canonicalSlug } from "@/utils/slug";
import DOMPurify from 'dompurify';
import defaultDP from"../icons/Avatar.svg";
import userDetailsServices from '../../services/userDetailsService';
export default {
  name: "FloatingView",

  props: {
    service: {
      type: Object,
      required: true
    },
  },

  data() {
    return {
      isMinimized: false,
      chatMessages: [],
      chatMessage: "",
      messages: [],
      currentUserId: null,
      isFirstMessage: true,
      showServiceBox: true,
      windowOrigin: window.location.href,
      profileImage: defaultDP,
      messageRefreshInterval: null,
    };
  },

  computed: {
      groupedMessages() {
      return this.messages.reduce((acc, message) => {
        const date = this.formatDateHeader(message.sendDate);
        if (!acc[date]) acc[date] = [];
        acc[date].push(message);
        return acc;
      }, {});
    },
    floatingViewStyle() {
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        return {
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: `max(0px, env(safe-area-inset-bottom, 0px))`,
          width: '100vw',
          height: this.isMinimized ? '60px' : '60vh',
          maxHeight: '95vh',
          zIndex: 10000,
          margin: 0,
          borderRadius: '18px 18px 0 0',
          boxShadow: '0px 24px 40px -4px rgba(10, 13, 18, 0.18), 0px 8px 16px -4px rgba(10, 13, 18, 0.08)'
        };
      } else {
        return {
          position: 'fixed',
          bottom: '10px',
          right: '10px',
          width: this.isMinimized ? '290px' : '300px',
          height: this.isMinimized ? '60px' : '458px',
          zIndex: 10000
        };
      }
    },
  },
  mounted() {
    
    console.log("Floating view received:", this.service);
    console.log("selected userid",this.service.userID)
    userDetailsServices.getProfileImage(this.service.userID).then((imgUrl) => {
      this.profileImage = imgUrl || defaultDP;
    });
    this.fetchChatMessages();
    // Check if polling is enabled via environment variable (default: true)
    const pollEnabled = import.meta.env.VITE_MESSAGE_POLL_ENABLED !== 'false';
    if (import.meta.env.DEV) {
      console.log('Message polling check on mount:', {
        VITE_MESSAGE_POLL_ENABLED: import.meta.env.VITE_MESSAGE_POLL_ENABLED,
        pollEnabled: pollEnabled
      });
    }
    if (pollEnabled) {
      const pollInterval = import.meta.env.VITE_MESSAGE_POLL_INTERVAL || 4000;
      this.messageRefreshInterval = setInterval(() => {
        this.fetchChatMessages();
      }, pollInterval);
    }

    // An expired token can never succeed on retry — stop polling rather than
    // 401ing every few seconds. See utils/sessionExpiry.js.
    window.addEventListener("session-expired", this.stopMessagePolling);
  },

  beforeUnmount() {
    this.stopMessagePolling();
    window.removeEventListener("session-expired", this.stopMessagePolling);
  },

  methods: {
    // Swap in the bundled avatar when an image URL fails to load. The flag stops
    // an error on the default itself from retriggering this handler forever
    // (img.src reports a resolved absolute URL, so comparing it to the bundled
    // path is not a reliable guard).
    useDefaultAvatar(event) {
      const img = event.target;
      if (img.dataset.fallbackApplied) return;
      img.dataset.fallbackApplied = "true";
      img.src = defaultDP;
      this.profileImage = defaultDP;
    },

    stopMessagePolling() {
      if (this.messageRefreshInterval) {
        clearInterval(this.messageRefreshInterval);
        this.messageRefreshInterval = null;
      }
    },

    goToBusinessProfile() {
      const userId = this.service?.userID;
      if (!userId) return;
      const userName = this.service?.userDetails?.userName;
      const slug = userName
        ? userName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
        : userId;
      this.$router.push({ name: 'BusinessProfile', params: { slug } });
    },
    formatMessage(message) {
      if (!message || typeof message !== "string") return "";

      // 1. Escape HTML entities to neutralise any injected tags
      const escaped = message
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

      // 2. Convert newlines
      let result = escaped.replace(/\n/g, "<br>");

      // 3. Markdown-style [text](url)
      const mdLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
      result = result.replace(
        mdLinkRegex,
        '<a href="$2" target="_blank" rel="noopener noreferrer" class="chat-link">$1</a>'
      );

      // 4. Plain URLs (not already inside an href)
      const plainLinkRegex = /(?<!href=")(https?:\/\/[^\s<]+)/g;
      result = result.replace(
        plainLinkRegex,
        '<a href="$1" target="_blank" rel="noopener noreferrer" class="chat-link">$1</a>'
      );

      // 5. Final sanitisation pass — allow only safe tags/attributes
      return DOMPurify.sanitize(result, {
        ALLOWED_TAGS: ["a", "br"],
        ALLOWED_ATTR: ["href", "target", "rel", "class"],
      });
    },
    formatDateHeader(dateString) {
      const options = { year: "numeric", month: "short", day: "numeric" };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },
    toggleMinimize() {
      this.isMinimized = !this.isMinimized;
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    async sendMessage() {
      if (this.chatMessage.trim()) {
        const userText = this.chatMessage.trim();
        const localuserid = localStorage.getItem("userid");
        let finalMessage = "";

        if (import.meta.env.DEV) {
          console.log("sendMessage -> serviceID:", this.service.servicesID);
          console.log("sendMessage -> userID:", this.service.userID);
        }

        if (this.isFirstMessage) {
          // Defensive ad link resolution (like ViewAds.vue)
          let URLserviceID = this.service.servicesID;
          let URLuserID = this.service.userID;
          let URLlink = null;
          try {
            // Try to resolve by serviceID first
            let res = await axios.post("/api/serviceslinklist", { serviceID: this.service.servicesID });
            if (res?.data?.items?.length && res.data.items[0].serviceID && res.data.items[0].userID && res.data.items[0].url) {
              URLserviceID = res.data.items[0].serviceID;
              URLuserID = res.data.items[0].userID;
              URLlink = res.data.items[0].url;
            } else {
              // Fallback: try by URL if available
              const shareUrl = window.location.href;
              res = await axios.post("/api/serviceslinklist", { url: shareUrl });
              if (res?.data?.items?.length && res.data.items[0].serviceID && res.data.items[0].userID && res.data.items[0].url) {
                URLserviceID = res.data.items[0].serviceID;
                URLuserID = res.data.items[0].userID;
                URLlink = res.data.items[0].url;
              }
            }
          } catch (err) {
            console.warn("serviceslinklist failed, using fallback IDs", err);
          }
          // Fallback slug if no URLlink
          if (!URLlink) {
            URLlink = canonicalSlug(this.service.servicesName) || "service";
          }
          localStorage.setItem(
            "viewPostContext",
            JSON.stringify({
              servicesID: URLserviceID,
              userID: URLuserID,
            })
          );
          // Emit markdown, not raw HTML: formatMessage() HTML-escapes the whole
          // message before linkifying (so users can't inject their own anchors),
          // which turned a literal <a> tag into visible markup. The [text](url)
          // form is the escape-safe path that formatter already understands, and
          // its regex requires an absolute URL.
          const adLink = `${WEB_ORIGIN}/view-post/${URLlink}`;
          finalMessage = `
📝Service Name: ${this.service.servicesName}
📍 Location: ${this.service.location}
🔗[View Ad](${adLink})

💬 ${userText}
`;
          this.isFirstMessage = false;
          this.showServiceBox = false;
        } else {
          finalMessage = userText;
        }

        const newMessage = {
          messageId: Date.now().toString(),
          sender: "me",
          message: finalMessage,
          sendDate: new Date().toISOString(),
          isRead: false,
          fromUserId: localuserid,
          toUserId: this.service.userID,
        };

        this.messages.push(newMessage);
        // Sort messages by sendDate ascending (oldest first)
        this.messages.sort((a, b) => new Date(a.sendDate) - new Date(b.sendDate));
        this.chatMessage = "";

        axios
          .post("/api/message", newMessage)
          .then((res) => {
            if (import.meta.env.DEV) {
              console.log("Message sent:", res);
            }
          })
          .catch((err) => {
            console.error("Send error:", err.message || err);
          });

        this.$nextTick(() => {
          const el = this.$refs.chatContainer;
          if (el) el.scrollTop = el.scrollHeight;
        });

        // Notify inside the block: finalMessage is scoped here, and there is
        // nothing to notify about when the input was empty.
        this.sendEmail(finalMessage);
      }
    },
    sendEmail(messageText) {
      if (import.meta.env.DEV) {
        console.log("User ID:", this.service.userID);
      }
      axios
        .post("/api/getoneusers", {
          userID: this.userID,
        })
        .then((response) => {
          if (import.meta.env.DEV) {
            console.log(" to email", response);
          }
          // getoneusers returns an ARRAY (utility.findAll), so reading
          // .email off the response object yielded undefined and the
          // notification always failed validation server-side.
          const recipientUser = Array.isArray(response.data)
            ? response.data[0]
            : response.data;
          const toEmail = recipientUser?.email;
          const touserName = recipientUser?.userName;
          if (import.meta.env.DEV) {
            console.log(" to email", toEmail, touserName);
          }
          axios
            .post("/api/getoneusers", {
              userID: localStorage.getItem("userid"),
            })
            .then(async (response) => {
              if (import.meta.env.DEV) {
                console.log("from email", response);
              }
              const fromEmail = response.data[0].email;
              const fromUserName = response.data[0].userName;
              if (import.meta.env.DEV) {
                console.log(" to email", fromEmail);
              }
              const recaptchaToken = await getRecaptchaToken('send_message');
              if (!toEmail) {
                if (import.meta.env.DEV) {
                  console.warn("send-addmessage skipped: no recipient email");
                }
                return;
              }

              axios
                .post("/api/send-addmessage", {
                  // Field names must match the handler, which destructures
                  // { toEmail, fromUserName, message }. The old payload used
                  // to/name/senderEmail and omitted message entirely, so every
                  // call 400'd with "Missing required fields".
                  toEmail,
                  fromUserName,
                  message: messageText,
                  recaptchaToken,
                })
                .then((response) => {
                  if (import.meta.env.DEV) {
                    console.log("send an email", response);
                  }
                });
            });
        });
    },

    fetchChatMessages() {
      const localuserid = localStorage.getItem("userid");
      const toUserId = this.service.userID;
      axios
        .post("/api/messagelist", {
          fromUserId: localuserid,
          toUserId: toUserId,
        })
        .then((response) => {
          this.messages = response.data.items
            .filter(
              (message) =>
                (message.fromUserId === localuserid &&
                  message.toUserId === toUserId) ||
                (message.fromUserId === toUserId &&
                  message.toUserId === localuserid)
            )
            .map((message) => {
              message.sender =
                message.fromUserId === localuserid ? "me" : "other";
              return message;
            });
          // Sort messages by sendDate ascending (oldest first)
          this.messages.sort((a, b) => new Date(a.sendDate) - new Date(b.sendDate));
          this.$nextTick(() => {
            const el = this.$refs.chatContainer;
            if (el) el.scrollTop = el.scrollHeight; // scroll to bottom
          });

          if (import.meta.env.DEV) {
            console.log("fetch messages from services", this.messages);
          }
        })
        .catch((error) => {
          console.error("Error fetching messages:", error.message || error);
        });
    }
  }
};
</script>

<style scoped>
.floating-view {
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 14px;
  box-shadow: 0px 24px 40px -4px rgba(10, 13, 18, 0.18),
    0px 8px 16px -4px rgba(10, 13, 18, 0.08);
  overflow: hidden;
  border: 1px solid rgba(52, 103, 213, 0.10);
}

/* HEADER */
.header-view {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  padding: 0 14px;
  background: linear-gradient(135deg, #2d5bbf 0%, #3467d5 50%, #5b87f0 100%);
  border-bottom: 3px solid rgba(255, 255, 255, 0.15);
  flex-shrink: 0;
}

.header-list {
  display: flex;
  align-items: center;
  gap: 10px;
}

.image-view img {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.45);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
}

.name {
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  letter-spacing: 0.01em;
}


.main-view {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #f5f7fb;
}

.message {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}

.message-incoming {
  flex-direction: row;
}

.message-outgoing {
  flex-direction: row-reverse;
}

.avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
}

.message-bubble {
  max-width: 70%;
  background-color: #f1f1f1;
  color: #181d27;
  border-radius: 12px;
  padding: 8px 12px;
  position: relative;
  word-wrap: break-word;
}

.message-outgoing .message-bubble {
  background-color: #3467d5;
  color: white;
}

.message-time {
  font-size: 10px;
    display: block;
  text-align: right;
}

.message-images {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 4px;
  margin-top: 8px;
}

.message-images img {
  width: 100%;
  border-radius: 8px;
  object-fit: cover;
}
.date-separator {
  display: flex;
  justify-content: center; /* Center horizontally */
  align-items: center;     /* Center vertically if needed */
  margin: 20px 0;          /* Space above and below */
  font-size: 0.85rem;      /* Smaller font size */
  color: #888;             /* Gray color */
  position: relative;
  text-align: center;
}


.date-separator::before,
.date-separator::after {
  content: "";
  flex: 1;
  height: 1px;
  margin: 0 10px;
}

.date-separator::before {
  margin-right: 10px;
}

.date-separator::after {
  margin-left: 10px;
}

.reply-link {
  color: #3467d5;        
  text-decoration: none;   
  font-weight: 500;       
}

.reply-link:hover {
  color: #1d4ed8;         
  text-decoration: underline; 
}



.bottom-view {
  display: flex;
  padding: 10px 12px;
  border-top: 1px solid #e5e9f2;
  background: #ffffff;
  flex-shrink: 0;
}

.bottom-box {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.input-box-text {
  flex: 1;
  height: 38px;
  padding: 0 12px;
  font-size: 13px;
  border: 1.5px solid #e5e9f2;
  border-radius: 20px;
  outline: none;
  font-family: "Inter", sans-serif;
  color: #181d27;
  background: #f8f9fc;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.input-box-text:focus {
  border-color: #3467d5;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(52, 103, 213, 0.10);
}

.btn-view {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3467d5 0%, #5b87f0 100%);
  border: none;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(52, 103, 213, 0.35);
  transition: all 0.2s ease;
}

.btn-view:hover {
  background: linear-gradient(135deg, #2857c0 0%, #4a76df 100%);
  box-shadow: 0 4px 12px rgba(52, 103, 213, 0.45);
  transform: scale(1.08);
}

.btn-view svg {
  display: block;
  flex-shrink: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* all icon buttons same size */
.icon-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  padding: 0;
  transition: background 0.15s ease;
}

.icon-btn:hover {
  background: rgba(255, 255, 255, 0.28);
}

.icon {
  width: 18px;
  height: 18px;
  margin-right:0px;
  filter: brightness(0) invert(1);
}
/* Service Preview Box */
.reply-preview {
  padding: 10px 14px;
  background: #eef4ff;
  border-bottom: 1px solid #d6e1ff;
}

.service-card {
  display: flex;
  justify-content: space-between;
  align-items: flex-start; /* top align text + button */
  background: #ffffff;
  border: 1px solid #d5d7da;
  border-radius: 10px;
  padding: 12px 14px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  gap: 10px;
}

.service-info {
  display: flex;
  flex-direction: column; /* stack service name + location vertically */
  gap: 4px;
}

.service-title {
  font-size: 15px;
  font-weight: 600;
  color: #182230;
}

.service-location {
  font-size: 13px;
  color: #5c6470;
}

.view-ad-btn {
  align-self: center; /* center vertically relative to service-info */
  font-size: 14px;
  font-weight: 500;
  color: #3467d5;
  text-decoration: none;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid #3467d5;
  transition: 0.25s;
}

.view-ad-btn:hover {
  background: #3467d5;
  color: #ffffff;
}
.profile-img-clickable{
  cursor: pointer;
}
/* Username and verify tick alignment */
.name-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* .verify-tick {
  width: 1em;
  height: 1em;
  margin-left: 4px;
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
} */
</style>


